import React, { useState } from 'react';
import { Box, Typography, Stack, CircularProgress, Alert, IconButton, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMetricBreakdowns } from '../hooks';
import { getRagColor } from '../utils';
import type { MetricsFilters } from '../api/types';

interface MetricBreakdownPanelProps {
  /** UI metric id, e.g. "sleep_performance" / "strain" / "recovery_score". */
  metricId: string;
  /** Plain-English metric name for the heading, e.g. "Sleep". */
  metricLabel: string;
  /** Unit suffix for values, e.g. "%". */
  unit?: string;
  filterParams: MetricsFilters;
  /** Defer fetching until the panel is opened. */
  enabled?: boolean;
  /** Override the default breakdown dimensions (department/age/gender/shift). */
  dimensions?: ReadonlyArray<{ groupBy: string; label: string }>;
}

/**
 * Inline drill-down showing a metric broken down by department / age / gender /
 * shift, each value colour-coded by its RAG band — e.g. "Sleep → Contact Team
 * 85% (green) / Team B 34% (red)". Reuses the comparison API via
 * useMetricBreakdowns. Metrics the API can't break down render a brief note.
 */
export const MetricBreakdownPanel: React.FC<MetricBreakdownPanelProps> = ({
  metricId,
  metricLabel,
  unit = '',
  filterParams,
  enabled = true,
  dimensions,
}) => {
  const theme = useTheme();
  const {
    supported,
    dimensions: breakdownDimensions,
    isLoading,
  } = useMetricBreakdowns(metricId, filterParams, { enabled, dimensions });
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (!supported) {
    return (
      <Alert severity='info' sx={{ mt: 1 }}>
        Demographic breakdowns aren't available for {metricLabel.toLowerCase()} yet.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  const dimensionsWithData = breakdownDimensions.filter((d) => d.rows.length > 0);

  if (dimensionsWithData.length === 0) {
    return (
      <Alert severity='info' sx={{ mt: 1 }}>
        No {metricLabel.toLowerCase()} breakdown data for the current filters.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Stack spacing={2}>
        {dimensionsWithData.map((dim) => (
          <Box key={dim.groupBy}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              {dim.label}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {dim.rows.map((row) => {
                const rowKey = `${dim.groupBy}-${row.groupKey}`;
                const hasChildren = !!row.children?.length;
                const expanded = expandedGroupKeys.has(rowKey);
                const color = row.ragColor ? getRagColor(row.ragColor, theme) : theme.palette.text.primary;
                return (
                  <Box key={rowKey}>
                    <Box
                      onClick={hasChildren ? () => toggleExpanded(rowKey) : undefined}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: hasChildren ? 'pointer' : 'default',
                      }}
                    >
                      {hasChildren ? (
                        <IconButton
                          size='small'
                          aria-label={expanded ? `Collapse ${row.label}` : `Expand ${row.label}`}
                          sx={{
                            p: 0,
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.15s',
                          }}
                        >
                          <ExpandMoreIcon fontSize='small' />
                        </IconButton>
                      ) : (
                        row.ragColor && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              flexShrink: 0,
                              backgroundColor: color,
                            }}
                          />
                        )
                      )}
                      <Typography variant='body2' sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {row.label}
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 700, color }}>
                        {row.value}
                        {unit}
                      </Typography>
                    </Box>
                    {hasChildren && expanded && (
                      <Stack spacing={0.5} sx={{ mt: 0.5, pl: 3.5 }}>
                        {row.children!.map((child) => {
                          const childColor = child.ragColor
                            ? getRagColor(child.ragColor, theme)
                            : theme.palette.text.primary;
                          return (
                            <Box
                              key={`${rowKey}-${child.groupKey}`}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                              {child.ragColor && (
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    backgroundColor: childColor,
                                  }}
                                />
                              )}
                              <Typography variant='caption' sx={{ flex: 1, minWidth: 0 }} noWrap>
                                {child.label}
                              </Typography>
                              <Typography variant='caption' sx={{ fontWeight: 700, color: childColor }}>
                                {child.value}
                                {unit}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
